import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container, Alert } from 'react-bootstrap';
import { authService, User } from '../services/authService';
import { ROUTES } from '../constants';
import { useLocale } from '../contexts/LocaleContext';

interface LayoutProps {
  children: React.ReactNode;
}

const THEME_STORAGE_KEY = 'app-theme';
type Theme = 'light' | 'dark';

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { locale, setLocale, t } = useLocale();
  const [user, setUser] = useState<User | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return (stored === 'dark' || stored === 'light') ? stored : 'light';
  });
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!navbarExpanded) return;
    const onDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (headerRef.current && !headerRef.current.contains(target)) {
        setNavbarExpanded(false);
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [navbarExpanded]);

  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 992) setNavbarExpanded(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadUser();
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('success')) {
      setSuccessMessage(urlParams.get('success'));
    }
    if (urlParams.get('error')) {
      setErrorMessage(urlParams.get('error'));
    }
  }, [location]);

  const loadUser = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  };

  const handleLogout = async () => {
    await authService.logout();
  };

  const handleLocaleChange = async (newLocale: string) => {
    await setLocale(newLocale as 'en' | 'ru');
  };

  const isSuperAdmin = user?.roles?.includes('Admin') || false;

  return (
    <>
      <header ref={headerRef} className={`app-header${navbarExpanded ? ' navbar-menu-open' : ''}`}>
        <Navbar expand="lg" className="navbar-light bg-light app-navbar" expanded={navbarExpanded} onToggle={setNavbarExpanded}>
          <Container>
            <Navbar.Brand
              as={Link}
              to={ROUTES.HOME}
              className="fw-bold text-decoration-none app-navbar-brand"
              style={{
                fontSize: '1rem',
                letterSpacing: '0',
                textTransform: 'uppercase',
              }}
            >
              <i className="bi bi-ticket-perforated me-1" style={{ fontSize: '1rem' }}></i>
              {t('promocodeGenerator')}
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="navbarText" />
            <Navbar.Collapse id="navbarText" className={isSuperAdmin ? 'justify-content-between' : 'justify-content-end'}>
              <Nav className="mr-auto">
                {user && (
                  <>
                    <Nav.Link as={Link} to={ROUTES.PROMOCODE_TEMPLATES}>
                      {t('templates')}
                    </Nav.Link>
                    {isSuperAdmin && (
                      <>
                        <Nav.Link as={Link} to={ROUTES.ADMIN_USERS}>
                          {t('users')}
                        </Nav.Link>
                        <Nav.Link as={Link} to={ROUTES.TELEGRAM_ACCESS}>
                          {t('telegramAccess')}
                        </Nav.Link>
                      </>
                    )}
                  </>
                )}
              </Nav>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                  onClick={() => setThemeState((t) => (t === 'light' ? 'dark' : 'light'))}
                  title={theme === 'light' ? t('theme.dark') : t('theme.light')}
                  aria-label={theme === 'light' ? t('theme.dark') : t('theme.light')}
                  style={{ width: 32, height: 32}}
                >
                  {theme === 'light' ? (
                    <i className="bi bi-moon-stars" aria-hidden />
                  ) : (
                    <i className="bi bi-sun" aria-hidden />
                  )}
                </button>
                <select
                  className="form-select form-select-sm me-2"
                  value={locale}
                  onChange={(e) => handleLocaleChange(e.target.value)}
                  style={{ width: '80px',height: 32 }}
                >
                  <option value="en">🇬🇧EN</option>
                  <option value="ru">🇷🇺RU</option>
                </select>
                {user ? (
                  <>
                    {user.roles && user.roles.length > 0 && (
                      <span className="ms-2 text-muted">{user.roles[0]}</span>
                    )}
                    <Link to={ROUTES.LOGIN} className="link-secondary text-decoration-none ms-2" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
                      {t('logout')}
                    </Link>
                  </>
                ) : (
                  <Nav.Link as={Link} to={ROUTES.LOGIN}>
                    Login
                  </Nav.Link>
                )}
              </div>
            </Navbar.Collapse>
          </Container>
        </Navbar>
      </header>
      <main>
        <Container>
          {successMessage && (
            <Alert variant="success" role="alert" dismissible onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert variant="danger" role="alert" dismissible onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}
          {children}
        </Container>
      </main>
      <script
        dangerouslySetInnerHTML={{
          __html: `
            const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
            const popoverList = [...popoverTriggerList].map(popoverTriggerEl => new bootstrap.Popover(popoverTriggerEl));
          `,
        }}
      />
    </>
  );
};

export default Layout;
